/* jscs:disable requireSpacesInsideJsxExpressionContainers */
import React from 'react';
import PropTypes from 'prop-types';
import { TrayMenu } from '@ux/tray-menu';
import { golf, breakpoints } from '../enums';
import Component from '@ux/component';
import { IntlProvider, FormattedMessage } from 'react-intl';
import UserDetails from './user-details';
import SignIn from './sign-in';
import CreateMyAccount from './create-account';
import QuickLinks from './quick-links';
import User from '@ux/icon/user';
import { url } from '@ux/header-util';

/**
 * Render and expose the AccountTray as a functional Component.
 *
 * @param {Object} props Properties
 * @returns {ReactElement} Namespaced AccountTray.
 * @api public
 */
class AccountTray extends Component {
  /**
   * Add eventListeners for breakpoints.
   *
   * @api private
   */
  componentDidMount() {
    this.breakpoint(...breakpoints);
  }

  /**
   * Remove the eventListeners of breakpoints.
   *
   * @api private
   */
  componentWillUnmount() {
    this.breakup();
  }

  /**
   * generates custom sso links for login and create account
   * @returns {Object} sso links
   */
  getSsoLinks() {
    const { sso, urls, ventureHomeRedirect, privateLabelId } = this.props;
    let loginLink = urls.sso.login.href;
    let createAccountLink = urls.sso.createAccount.href;

    if (ventureHomeRedirect === 'variant' && privateLabelId === 1) {
      const ssoParams = {
        path: 'vh-login-redirect',
        app: 'dashboard.api'
      };
      loginLink = url.updateQuery(loginLink, ssoParams);
      createAccountLink = url.updateQuery(createAccountLink, ssoParams);
    }

    if (sso) {
      const { path, app } = sso;
      if (path) {
        loginLink = url.updateQuery(loginLink, {
          path
        });
        createAccountLink = url.updateQuery(createAccountLink, {
          path
        });
      }
      if (app) {
        loginLink = url.updateQuery(loginLink, {
          app
        });
        createAccountLink = url.updateQuery(createAccountLink, {
          app
        });
      }
    }

    return {
      createAccountLink,
      loginLink
    };
  }

  render() {
    const props = this.props;
    let name;

    if (!this.isMobile) {
      if (props.loggedIn) {
        // props.displayName is the short name (e.g. "Jane").
        name = props.displayName;
      } else {
        name = <FormattedMessage id={ golf.login } />;
      }
    }

    const trayName = (
      <span className='account-tray-name'>
        <span className='user-icon'>
          <User width={ 28 } height={ 28 } />
        </span>
        <span className='title-text'>{ name }</span>
      </span>
    );

    const ssoLinks = this.getSsoLinks();

    return (
      <IntlProvider locale={ props.market } messages={ props.messages }>
        <TrayMenu
          type='base'
          className={ this.classNames('account-tray', {
            'is-pro': props.loggedIn && props.isPro
          }) }
          name={ trayName }
          data-eid='uxp.hyd.shared.utility_bar.account_tray.click'>
          <div
            className={ props.oldBrand ? 'container' : 'container-fluid' }>
            <div className='row'>
              <div className='col-xl-7 push-xl-5 col-lg-6 push-lg-6 mb-5'>
                <UserDetails { ...props } />
                {!props.loggedIn && (
                  <div className='row'>
                    <SignIn { ...props } { ...ssoLinks } />
                    <CreateMyAccount { ...props } { ...ssoLinks } />
                  </div>
                )}
              </div>
              <QuickLinks { ...props } />
            </div>
          </div>
        </TrayMenu>
      </IntlProvider>
    );
  }
}

/**
 * Default properties for account tray.
 *
 * @type {Object}
 * @api public
 */
AccountTray.defaultProps = {
  urls: {
    products: 'https://mya.godaddy.com',
    renewals: 'https://account.godaddy.com/billing?filter=expires&subFilter=90',
    account: 'https://account.godaddy.com',
    pro: 'https://pro.godaddy.com',
    manageDomains: 'https://dcc.godaddy.com',
    manageHosting: 'https://gateway.godaddy.com',
    sso: {
      o365Login: 'https://sso.godaddy.com',
      webmailLogin: 'https://email.godaddy.com',
      login: 'https://sso.godaddy.com?realm=idp&app=mya&path=',
      logout: 'https://sso.godaddy.com/logout?realm=idp',
      createAccount:
        'https://sso.godaddy.com/account/create?realm=idp&app=www&path='
    }
  },
  market: 'en-US',
  loggedIn: false,
  isPro: false,
  privateLabelId: 1
};

/**
 * Require properties.
 *
 * @type {Object}
 * @api public
 */
AccountTray.propTypes = {
  oldBrand: PropTypes.bool,
  sso: PropTypes.object,
  urls: PropTypes.object.isRequired,
  loggedIn: PropTypes.bool,
  privateLabelId: PropTypes.number,
  name: PropTypes.string,
  displayName: PropTypes.string,
  shopperId: PropTypes.string,
  isPro: PropTypes.bool,
  market: PropTypes.string.isRequired,
  messages: PropTypes.object.isRequired,
  vip: PropTypes.object,
  ventureHomeRedirect: PropTypes.bool
};

export default AccountTray;
